import { t } from 'i18next';
import { useDefaultImages } from 'providers/default-image-provider';
import { useEffect, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { MdCameraAlt } from 'react-icons/md';
import CircleButton from 'widgets/controls/buttons/circle-button';
import GenericDialog from 'widgets/dialogs/generic';

interface AvatarEditorProps {
  avatarUrl?: string;
  placeholder?: 'avatar' | 'logo';
  onChange: (base64: string) => void;
}

const AvatarEditorComponent: React.FC<AvatarEditorProps> = ({ avatarUrl, onChange, placeholder = 'avatar' }) => {
  const [avatar, setAvatar] = useState<string | null>(avatarUrl || null);
  const [originalAvatar, setOriginalAvatar] = useState<string | null>(avatarUrl || null);
  const [isEditing, setIsEditing] = useState(false);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<AvatarEditor>(null);
  const { defaultAvatar, defaultLogo } = useDefaultImages();

  useEffect(() => {
    setAvatar(avatarUrl || null);
    setOriginalAvatar(avatarUrl || null);
  }, [avatarUrl]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          setOriginalAvatar(avatar);
          setAvatar(reader.result as string);
          setIsEditing(true);
          setScale(1);
          setRotation(0);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const base64 = canvas.toDataURL('image/png');
      setAvatar(base64);
      onChange(base64);
      setIsEditing(false);
    }
  };

  const onCameraButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (isEditing) {
    return (
      <GenericDialog
        isOpen={true}
        size="md"
        title={t('avatar.dialog.title')}
        onClose={() => {
          setAvatar(originalAvatar);
          setIsEditing(false);
        }}
        onSave={handleSave}
      >
        <div className="my-8 flex w-full flex-col items-center gap-4">
          <AvatarEditor
            ref={editorRef}
            image={avatar || ''}
            width={300}
            height={300}
            border={50}
            borderRadius={150}
            color={[0, 0, 0, 0.6]}
            scale={scale}
            rotate={rotation}
            style={{
              backgroundColor: '#f3f4f6',
              boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
            }}
          />
        </div>
        <div className="m-2 flex items-center justify-center gap-4">
          <div className="flex-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">{t('avatar.dialog.zoom')}</label>
            <input
              type="range"
              min="1"
              max="2"
              step="0.01"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 text-tertiary"
            />
          </div>
          <div className="flex-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">{t('avatar.dialog.rotation')}</label>
            <input
              type="range"
              min="-180"
              max="180"
              step="1"
              value={rotation}
              onChange={(e) => setRotation(parseInt(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 text-tertiary"
            />
          </div>
        </div>
      </GenericDialog>
    );
  }

  return (
    <div className="relative flex h-[100px] w-[100px] items-center justify-center">
      <div className="relative h-[100px] w-[100px] overflow-hidden rounded-full">
        <img
          src={avatar || (placeholder === 'avatar' ? (defaultAvatar as string) : (defaultLogo as string))}
          alt="Avatar"
          width={100}
          height={100}
          className="h-full w-full object-cover"
        />
      </div>
        <div className="absolute -right-[2px] -top-[2px]">
          <CircleButton icon={MdCameraAlt} onClick={onCameraButtonClick} size="sm" extra="bg-gray-100" />
        </div>
      <input type="file" accept="image/*" onChange={handleAvatarChange} ref={fileInputRef} style={{ display: 'none' }} />
    </div>
  );
};

export default AvatarEditorComponent;
